// src/TrackOrder.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./contexts/CartContext";
import { useTheme } from "./contexts/ThemeContext";
import { ShoppingCart, Home } from "lucide-react";
import logo from "./assets/logo.png";

import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase";



export default function TrackOrder() {
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { darkMode, setDarkMode } = useTheme();

  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");

async function handleTrack() {
  if (!orderId.trim() || !email.trim()) {
    alert("Please enter both Order Number and Email to track your order.");
    return;
  }

  // Basic email format validation (very reliable)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    alert("Please enter a valid email address.");
    return;
  }

  try {
    // Lookup order by orderNumber
    const q = query(
      collection(db, "orders"),
      where("orderNumber", "==", orderId)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      alert("❌ No order found with this Order Number.");
      return;
    }

    const orderData = snapshot.docs[0].data();

    // Compare email provided with order email
    if (orderData.customer?.email?.toLowerCase() !== email.toLowerCase()) {
      alert("❌ Coudld not retrieve a matching Order.");
      return;
    }

    // All good — navigate
    navigate(`/order/${orderId}`);

  } catch (err) {
    console.error("Error tracking order:", err);
    alert("Something went wrong. Please try again later.");
  }
}


  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 dark:from-gray-900 dark:to-gray-800 text-gray-800 dark:text-gray-200 font-sans">

        {/* ===== HEADER ===== */}
        <header className="w-full h-16 md:h-20 shadow-sm bg-white/80 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-6xl mx-auto flex items-center justify-between h-full px-4 gap-4">

            {/* Mobile Home Button */}
            <button onClick={() => navigate("/")} className="md:hidden p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition">
              <Home size={22} />
            </button>

            {/* LOGO */}
            <div onClick={() => navigate("/")} className="flex items-center gap-3 cursor-pointer">
              <img src={logo} alt="Bubble Butter Logo" className="h-14 md:h-16 object-contain" />
              <h1 className="text-xl md:text-2xl font-bold text-pink-600 dark:text-pink-300">
                Bubble Butter
              </h1>
            </div>

            {/* DESKTOP NAV */}
            <div className="hidden md:flex items-center gap-4">
              <button onClick={() => navigate("/")} className="hover:text-pink-600 dark:hover:text-pink-300">Home</button>
              <button onClick={() => navigate("/products")} className="hover:text-pink-600 dark:hover:text-pink-300">Products</button>

              <button onClick={() => navigate("/cart")} className="relative px-3 py-2 rounded-lg bg-pink-600 text-white shadow">
                <ShoppingCart size={18} />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs px-2 rounded-full font-bold">
                    {itemCount}
                  </span>
                )}
              </button>

              {/* THEME TOGGLE WITH SUN/MOON ICONS */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition shadow-sm"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>

            {/* MOBILE CART */}
            <div className="md:hidden flex items-center gap-3">
              <button onClick={() => navigate("/cart")} className="relative p-2 rounded-lg bg-pink-600 text-white shadow">
                <ShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-xs px-1.5 rounded-full font-bold">
                    {itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        {/* ===== TRACK ORDER SECTION ===== */}
        <main className="max-w-6xl mx-auto px-4 mt-10 pb-32">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-pink-600 dark:text-pink-300 mb-6">
            Track Your Order
          </h2>

          <div className="max-w-md mx-auto bg-white dark:bg-gray-800 shadow rounded-2xl p-6 space-y-4">
            {/* Order ID */}
            <label className="block font-semibold">Order Number *</label>
            <input
              type="text"
              placeholder="e.g. BB-12345"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 focus:outline-none"
            />

            {/* Email */}
            <label className="block font-semibold">Email Address *</label>
            <input
              type="email"
              placeholder="e.g. you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 focus:outline-none"
            />

            {/* Track Button */}
            <button
              onClick={handleTrack}
              className="w-full py-3 rounded-xl bg-pink-600 hover:bg-pink-700 text-white transition font-medium shadow"
            >
              Track Order
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
